// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — one match, decoupled from Trystero, the DOM and the clock (driven by submit() /
// receive() / tick()). That decoupling is what makes the host-transfer and disconnect contracts
// testable with no network at all — see tests/takeover.test.ts.
//
// EVERY MESSAGE CARRIES THE ENTIRE MOVE LOG, not the latest move. A whole match is at most 72 small
// integers, so the wire cost is nothing, and in exchange a dropped datagram becomes unobservable:
// the next message reconstructs the world. The classic lockstep hang — "peer A sent move 11, it was
// lost on a turn boundary, and now both peers wait for each other for ever" — cannot occur, because
// there is no delta to lose. A 2s heartbeat re-sends the same log, so a peer that goes quiet
// self-heals within one beat and a spectator arriving at move 30 is instantly in sync.
//
// NOBODY IS AUTHORITATIVE. `derive()` is a pure function of (seed, mode, players, log), so the host
// has no state anyone else lacks and host transfer is a repaint. What the room DOES need is a way to
// keep going when a seat stops answering, and that is the fill: after the turn has been open 42s and
// that seat has been silent 15s, EVERY peer independently runs the same node-capped bot and appends
// the same move under the same key. Byte-identical, so the merge dedupes it. A time-boxed search
// here would be a live desync, which is why bot.ts counts nodes and never milliseconds.

import { FILL_TIER, chooseMove, tierOf, type Tier } from './bot';
import {
  RULES_REV,
  applyMove,
  canonicalLog,
  createState,
  derive,
  digest,
  keyOf,
  seatToPlay,
  tallies,
  winners,
  type RuleEvent,
  type State,
} from './rules';
import { modeOf, type Mode } from './modes';
import type { Tally } from './score';

export interface WireMsg {
  /** Round number of the MATCH (not the game's internal round) so a stale message cannot disturb us. */
  r: number;
  /** The whole log. */
  l: number[];
  /** FNV digest of the state after replaying it. */
  d: number;
  /** Rules revision — a mismatch is a stale cached build, not a desync, and says so. */
  v: number;
}

export type Fault = null | 'desync' | 'build';

/** Your own turn plays itself at this point. Comfortably inside FILL_OPEN_MS, so the fill below
 *  only ever fires for a seat that is genuinely gone rather than one that is merely slow. */
export const TURN_MS = 30_000;
const FILL_OPEN_MS = 42_000;
/**
 * How long a seat may go silent before the room stops trusting `onPeerLeave`.
 *
 * Learned across this fleet: when a tab goes away abruptly Trystero may never report the peer as
 * gone, so anything armed only from that event never arms, and the survivors sit for ever looking at
 * a live board. A present peer heartbeats every 2s, so fifteen seconds of nothing is not a slow
 * player — and this makes the fill reachable without the transport noticing anything at all.
 */
const FILL_SILENCE_MS = 15_000;

export interface ResultRow {
  seat: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  won: boolean;
  tally: Tally;
  /** Doublets this seat had to throw away, and the coronets that went with them. */
  spoiled: number;
  /** Whether this seat stopped answering and was played out by the room. */
  absent: boolean;
}

export interface MatchDeps {
  send: (m: WireMsg) => void;
  onChange: () => void;
  onEnd: () => void;
  onEvents: (events: RuleEvent[], s: State) => void;
}

export interface MatchOpts {
  modeId: string;
  seed: number;
  players: number;
  round: number;
  names: string[];
  /** null = spectator. */
  mySeat: number | null;
  /** Solo only: which seats a local bot plays, and at what strength. */
  bots: Array<Tier | null>;
  deps: MatchDeps;
  /** Networked matches run the turn clock and the absent-seat fill; solo does not. */
  networked?: boolean;
}

export class Match {
  state: State;
  readonly mode: Mode;
  readonly names: string[];
  readonly mySeat: number | null;
  readonly bots: Array<Tier | null>;
  readonly networked: boolean;
  readonly round: number;
  fault: Fault = null;
  /** Ms this turn has been open, and per-seat ms since anything was heard from them. */
  turnOpen = 0;
  silence: number[];
  absent: boolean[];
  private ended = false;
  deps: MatchDeps;

  constructor(o: MatchOpts) {
    this.mode = modeOf(o.modeId);
    this.state = createState(o.seed, o.modeId, o.players);
    this.names = o.names.slice();
    this.mySeat = o.mySeat;
    this.bots = o.bots.slice();
    this.deps = o.deps;
    this.round = o.round;
    this.networked = o.networked ?? false;
    this.silence = new Array(o.players).fill(0);
    this.absent = new Array(o.players).fill(false);
  }

  get over(): boolean {
    return this.state.over;
  }

  get turn(): number {
    return seatToPlay(this.state);
  }

  get isMyTurn(): boolean {
    return this.mySeat !== null && this.turn === this.mySeat && !this.state.over && this.fault === null;
  }

  /** True while the seat to act is a local bot rather than a person. */
  get isBotTurn(): boolean {
    const t = this.turn;
    return t >= 0 && !this.state.over && this.fault === null && this.bots[t] !== null && this.bots[t] !== undefined;
  }

  /** Commit a move chosen locally — by the player, a local bot, or the clock. */
  submit(move: number): boolean {
    if (this.state.over || this.fault !== null) return false;
    const events: RuleEvent[] = [];
    if (!applyMove(this.state, move, events)) return false;
    this.turnOpen = 0;
    this.deps.onEvents(events, this.state);
    this.broadcast();
    this.deps.onChange();
    this.checkEnd();
    return true;
  }

  /** Re-send the whole log. Cheap, idempotent, and the reason a lost message is not a hang. */
  broadcast(): void {
    if (!this.networked) return;
    this.deps.send({ r: this.round, l: this.state.log.slice(), d: digest(this.state), v: RULES_REV });
  }

  /** Anything at all from a seat counts as "still there", including a heartbeat we already have. */
  noteSeat(seat: number): void {
    if (seat < 0 || seat >= this.silence.length) return;
    this.silence[seat] = 0;
    if (this.absent[seat]) {
      this.absent[seat] = false;
      this.deps.onChange();
    }
  }

  receive(msg: WireMsg, fromSeat = -1): void {
    if (!msg || msg.r !== this.round || this.fault !== null) return;
    this.noteSeat(fromSeat);
    if (msg.v !== RULES_REV) {
      this.fail('build');
      return;
    }
    const incoming = Array.isArray(msg.l) ? msg.l : [];
    const merged = canonicalLog([...this.state.log, ...incoming]);
    const mine = this.state.log;

    // The common case by far: they are level with us, or behind. Nothing to do but check we agree.
    if (merged.length === mine.length) {
      if (incoming.length === mine.length && typeof msg.d === 'number' && msg.d !== digest(this.state)) this.fail('desync');
      return;
    }

    // A strict extension of our log — apply just the new moves so the juice layer sees them as
    // events. Anything else (a reordering, a key we already hold with a different payload) is rare
    // and rebuilt from scratch, which is always correct because derive() is order-independent.
    const extension = mine.every((m, i) => m === merged[i]);
    const events: RuleEvent[] = [];
    if (extension) {
      for (let i = mine.length; i < merged.length; i++) {
        if (!applyMove(this.state, merged[i], events)) {
          this.fail('desync');
          return;
        }
      }
    } else {
      const rebuilt = derive(this.state.seed, this.state.modeId, this.state.players, merged);
      if (rebuilt.log.length !== merged.length) {
        this.fail('desync');
        return;
      }
      this.state = rebuilt;
    }

    if (typeof msg.d === 'number' && incoming.length === this.state.log.length && msg.d !== digest(this.state)) {
      this.fail('desync');
      return;
    }
    this.turnOpen = 0;
    if (events.length) this.deps.onEvents(events, this.state);
    this.deps.onChange();
    this.checkEnd();
  }

  /**
   * Advance the wall clock. Everything time-based lives here and is driven by setInterval from the
   * screen — never rAF, which a backgrounded tab throttles to nothing.
   */
  tick(dtMs: number): void {
    if (this.state.over || this.fault !== null) return;
    if (!this.networked) return;
    this.turnOpen += dtMs;
    for (let p = 0; p < this.silence.length; p++) if (p !== this.mySeat) this.silence[p] += dtMs;

    const seat = this.turn;
    if (seat < 0) return;

    // Your own turn: never a forfeit and never a freeze — your own peer plays your best move, so it
    // comes from the same place every other move comes from and lockstep is untouched.
    if (seat === this.mySeat) {
      if (this.turnOpen >= TURN_MS) this.submit(chooseMove(this.state, tierOf(FILL_TIER)));
      else this.deps.onChange();
      return;
    }

    // Somebody else's turn. Fill it only when it has been open a long time AND that seat has gone
    // quiet — the conjunction is what stops a slow player being played for.
    if (this.turnOpen >= FILL_OPEN_MS && this.silence[seat] >= FILL_SILENCE_MS) {
      if (!this.absent[seat]) this.absent[seat] = true;
      const move = chooseMove(this.state, tierOf(FILL_TIER));
      if (move >= 0) this.submit(move);
      return;
    }
    this.deps.onChange();
  }

  /** A peer left the room. Mark it — the fill still needs the silence timer to agree. */
  seatLeft(seat: number): void {
    if (seat < 0 || seat >= this.silence.length) return;
    this.silence[seat] = Math.max(this.silence[seat], FILL_SILENCE_MS);
    this.deps.onChange();
  }

  /**
   * Host transfer. There is genuinely nothing to take over — the state is derived, not held — so
   * this exists to make that explicit and to re-broadcast, which shortens the window in which a
   * newly promoted peer's neighbours are waiting on a log they already have.
   */
  onHostChanged(): void {
    this.broadcast();
    this.deps.onChange();
  }

  private fail(kind: Exclude<Fault, null>): void {
    if (this.fault !== null) return;
    this.fault = kind;
    // A fault ENDS the match rather than only setting a flag and repainting, which would leave every
    // peer on a frozen board with a one-line explanation and no way forward.
    this.deps.onChange();
    this.checkEnd();
  }

  private checkEnd(): void {
    if (!(this.state.over || this.fault !== null) || this.ended) return;
    this.ended = true;
    this.deps.onEnd();
  }

  /** Whose move key the room is waiting on — used by the smoke test and `?netdebug=1`. */
  get waitingKey(): number {
    return keyOf(0) + this.state.round * 8 + this.state.slot;
  }

  /** Every player's outcome, always — never just the local one. */
  results(): ResultRow[] {
    const t = tallies(this.state);
    const win = this.fault === null ? winners(this.state) : [];
    return t.map((tally, seat) => ({
      seat,
      name: this.names[seat] ?? `Seat ${seat + 1}`,
      isSelf: this.mySeat === seat,
      isBot: !!this.bots[seat],
      won: win.includes(seat),
      tally,
      spoiled: this.state.discards[seat],
      absent: this.absent[seat],
    }));
  }
}
